import { useState, useEffect, useCallback, FC } from "react";
import { useParams } from "react-router-dom";

import { App, Button, Card, Empty, Image, Typography } from "antd";

import { api } from "../../../services/api";
import { IService, IServicePhoto } from "../../../types";

import { Content } from "../../../components/Content";
import { PageHeader } from "../../../components/PageHeader";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { PhotoModal } from "../components/PhotoModal";

const getServicePhotos = async (
  orderId: string,
  serviceId: string,
  serviceItemId?: string
) => {
  return await api.get<IService>(
    `/service-orders/${orderId}/services/${serviceId}/photos`,
    {
      params: {
        serviceItemId,
      },
    }
  );
};

const OrderServicePhotos: FC = () => {
  const { id, serviceId, serviceItemId } = useParams();
  const { modal, message } = App.useApp();

  const [servicePhotos, setServicePhotos] = useState<IServicePhoto[]>();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [load, setLoad] = useState(false);

  const handleChangePage = useCallback(async () => {
    getServicePhotos(String(id), String(serviceId), serviceItemId)
      .then(({ data }) => {
        setServicePhotos(data.servicePhotos);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [id, serviceId, serviceItemId]);

  useEffect(() => {
    handleChangePage();
  }, [handleChangePage, load]);

  const [selectedPhoto, setSelectedPhoto] = useState<IServicePhoto | undefined>(
    undefined
  );

  const handleShowModal = (record?: IServicePhoto) => {
    setSelectedPhoto(record);
    setIsModalOpen(true);
  };

  const handleCloseModal = (refresh?: boolean) => {
    if (refresh) {
      setLoad(!load);
    }
    setSelectedPhoto(undefined);
    setIsModalOpen(false);
  };

  const onDeleteServicePhoto = async (photoId: string) => {
    return modal.confirm({
      title: "Deseja deletar essa foto?",
      content: <>A foto será excluída permanentemente!</>,
      okText: "Deletar",
      okButtonProps: {
        type: "primary",
        danger: true,
      },
      cancelText: "Cancelar",
      cancelButtonProps: {
        type: "default",
        danger: true,
      },
      onOk: async () => {
        await api
          .delete(
            `/service-orders/${id}/services/${serviceId}/photos/${photoId}`
          )
          .then(() => {
            message.success("Foto deletada com sucesso!");
            handleChangePage(); // Recarrega as fotos
          })
          .catch(() =>
            message.error("Ops! Algo deu errado ao deletar a foto!")
          );
      },
    });
  };

  return (
    <>
      <PageHeader
        style={{ boxShadow: "none", padding: 0 }}
        backButton={false}
        title={"Fotos"}
        actions={[
          <Button
            key={"add_photo"}
            type="primary"
            onClick={() => handleShowModal()}
            icon={<PlusOutlined />}
          >
            Adicionar Foto
          </Button>,
        ]}
      />
      <PhotoModal
        isModalOpen={isModalOpen}
        onClose={handleCloseModal}
        data={selectedPhoto}
      />
      <Content
        style={{
          padding: 0,
        }}
      >
        {servicePhotos?.length === 0 && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
        <Image.PreviewGroup
          preview={{
            maxScale: 4,
          }}
        >
          <div style={{ display: "flex", gap: 16 }}>
            {servicePhotos?.map((servicePhoto) => {
              return (
                <Card
                  key={servicePhoto.id}
                  hoverable
                  style={{
                    width: 300,
                  }}
                  cover={
                    <Image
                      height={250}
                      style={{ objectFit: "cover" }}
                      src={process.env.REACT_APP_ENDPOINT! + servicePhoto.photo}
                    />
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography.Paragraph
                      strong
                      ellipsis={{ rows: 2, expandable: true, symbol: "mais" }}
                    >
                      {servicePhoto.description}
                    </Typography.Paragraph>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        type="text"
                        onClick={() => handleShowModal(servicePhoto)}
                      >
                        <EditOutlined />
                      </Button>

                      <Button
                        type="text"
                        danger
                        onClick={() => onDeleteServicePhoto(servicePhoto.id)}
                      >
                        <DeleteOutlined />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </Image.PreviewGroup>
      </Content>
    </>
  );
};

export { OrderServicePhotos };
